import { db } from "@utils/IndexedDb";
import { ReadonlyURLSearchParams } from "next/navigation";
import React from "react";
import { ControllerRenderProps } from "react-hook-form";

import { CommercetoolsProject, PRODUCT_TYPE, SearchParams } from "@/types";

export const SEARCH_PAGE = "search-page";
export const SEO_SALE_PAGE = "seo-sale-page-search";
export const DEFAULT_SORT = "deliveryDate";

export const SEARCH_BAR_AS_PER_PAGE = [SEARCH_PAGE, SEO_SALE_PAGE];

export const allowedSearchParams = [
  "locale",
  "category",
  "type",
  "propertyType",
  "bedrooms",
  "bathrooms",
  "minimumUnitPrice",
  "maximumUnitPrice",
  "minimumUnitArea",
  "maximumUnitArea",
  "status",
  "isFurnished",
  "unitViews",
  "features",
  "deliveryDate",
  "launchDate",
  "lifeStyle",
  "brands",
  "others",
  "offers",
  "limit",
  "offset",
  "sort",
  "withResults",
  "withFilters",
  "withLocations",
  "customIDList",
  "currency",
  "measurement",
  "price",
  "area",
  "year",
  "onlyAvailable",
  "onlineExclusive",
  "vTour",
  "webIE",
];

export const controlledCheckbox = (field: ControllerRenderProps | undefined, event: React.ChangeEvent<HTMLInputElement>) => {
  if (!!field) {
    // const value = event.target.value.includes(",") ? event.target.value.split(",") : event.target.value;
    let currentValues: string[] = Array.isArray(field.value) ? field.value : [field.value];
    if (event.target.checked) {
      if (event.target.value.includes(",")) {
        currentValues = currentValues.concat(event.target.value.split(","));
      } else {
        currentValues.push(event.target.value);
      }
    } else {
      if (event.target.value.includes(",")) {
        // currentValues = currentValues.filter((item: string) => item !== event.target.value);
        currentValues = currentValues.filter((item: string) => !event.target.value.split(",").includes(item));
      } else {
        currentValues = currentValues.filter((item: string) => item !== event.target.value);
      }
    }
    currentValues = [...new Set(currentValues.filter((item) => !!item))];
    field.onChange(currentValues);
  }
};

export const controlledRadio = (field: ControllerRenderProps | undefined, event: React.ChangeEvent<HTMLInputElement>) => {
  if (!!field) {
    field.onChange(event.target.value);
  }
};

export const parseFilters = async (type: string, filters: string[], currency: string, measurement: string) => {
  let table;
  if (type === "project") {
    table = db.PROJECTS;
  } else {
    table = db.PROPERTY_TYPE;
  }
  const dbFilters: Record<string, any> = {};
  const rawValues = await table.toArray();
  const fullFilters = [...new Set([...filters, "price", "area", "vTour", "webIE"])];
  rawValues.forEach((item) => {
    fullFilters.forEach((filter) => {
      if (!dbFilters[filter]) dbFilters[filter] = [];
      switch (filter) {
        case "area":
          if (!dbFilters.areaMin) dbFilters.areaMin = [];
          if (!dbFilters.areaMax) dbFilters.areaMax = [];
          item?.areaMinConversion?.[measurement] &&
            item.areaMinConversion[measurement] > 0 &&
            dbFilters.areaMin.push(item.areaMinConversion[measurement]);
          item?.areaMaxConversion?.[measurement] &&
            item.areaMaxConversion[measurement] > 0 &&
            dbFilters.areaMax.push(item.areaMaxConversion[measurement]);
          break;
        case "price":
          if (!dbFilters.priceMin) dbFilters.priceMin = [];
          if (!dbFilters.priceMax) dbFilters.priceMax = [];
          item?.priceMaxCurrencies?.[currency] && item.priceMaxCurrencies[currency] > 0 && dbFilters.priceMax.push(item.priceMaxCurrencies[currency]);
          item?.priceMinCurrencies?.[currency] && item.priceMinCurrencies[currency] > 0 && dbFilters.priceMin.push(item.priceMinCurrencies[currency]);
          break;
        case "vTour":
          if (!dbFilters?.vTour) dbFilters.vTour = [];
          dbFilters?.vTour?.push(item?.vTour);
        case "webIE":
          if (!dbFilters?.webIE) dbFilters.webIE = [];
          dbFilters?.webIE?.push(item?.webIE);
        case "status":
          if (!!item[filter as keyof CommercetoolsProject]) {
            dbFilters[filter].push(item[filter as keyof CommercetoolsProject]);

            if (!dbFilters[filter].includes("any")) {
              dbFilters[filter].unshift("any");
            }
          }
          break;
        case "isFurnished":
          if (!!item[filter as keyof CommercetoolsProject]) {
            dbFilters[filter].push(item[filter as keyof CommercetoolsProject]);

            if (!dbFilters[filter].includes("any")) {
              dbFilters[filter].unshift("any");
            }
          }
          break;

        case "others":
          if (!dbFilters.others) dbFilters.others = [];
          item?.goldenVisa && !dbFilters.others.includes("goldenVisa") && dbFilters.others.push("goldenVisa");
          break;
        case "onlineExclusive":
          if (!dbFilters.exclusive) dbFilters.exclusive = [];
          dbFilters["onlineExclusive"].push(item.exclusive as keyof CommercetoolsProject);
          break;
        default:
          if (!!item[filter as keyof CommercetoolsProject]) {
            dbFilters[filter].push(item[filter as keyof CommercetoolsProject]);
          }
          break;
      }
    });
  });
  Object.entries(dbFilters).forEach(([key, value]) => {
    let flatValue = value.flat();
    switch (key) {
      case "deliveryDate":
        flatValue = flatValue
          .map((valueItem: Date) => {
            return valueItem.getFullYear();
          })
          .filter((year: number) => year !== 9999)
          .sort()
          .map((item: number) => item.toString());
        break;
      case "area":
        dbFilters.areaMin = dbFilters.areaMin.filter((item: number) => !isNaN(item) && item > 0).sort((a: number, b: number) => a - b);
        dbFilters.areaMax = dbFilters.areaMax.filter((item: number) => !isNaN(item) && item > 0).sort((a: number, b: number) => a - b);
        flatValue = [Math.round(dbFilters.areaMin[0]), Math.round(dbFilters.areaMax.at(-1))];
        break;
      case "price":
        dbFilters.priceMin = dbFilters.priceMin.filter((item: number) => !isNaN(item) && item > 0).sort((a: number, b: number) => a - b);
        dbFilters.priceMax = dbFilters.priceMax.filter((item: number) => !isNaN(item) && item > 0).sort((a: number, b: number) => a - b);
        flatValue = [Math.round(dbFilters.priceMin[0]), Math.round(dbFilters.priceMax.at(-1))];
        break;
      default:
        flatValue = flatValue.sort();
        break;
    }
    dbFilters[key] = [...new Set([...flatValue])];
  });
  return dbFilters;
};

export const queryStringToParams = (querystring: ReadonlyURLSearchParams): Record<string, any> => {
  const searchParams: Record<string, any> = {};
  const entries = Object.fromEntries(querystring);
  Object.entries(entries).forEach(([key, value]) => {
    switch (key) {
      case "category":
      case PRODUCT_TYPE.PROPERTY_TYPE:
      case "bedrooms":
        searchParams[key] = value.split(",");
        break;
      case "minimumUnitPrice":
        if (!searchParams.price) searchParams.price = [];
        searchParams.price[0] = parseInt(value);
        break;
      case "maximumUnitPrice":
        if (!searchParams.price) searchParams.price = [];
        searchParams.price[1] = parseInt(value);
        break;
      case "minimumUnitArea":
        if (!searchParams.area) searchParams.area = [];
        searchParams.area[0] = parseInt(value);
        break;
      case "maximumUnitArea":
        if (!searchParams.area) searchParams.area = [];
        searchParams.area[1] = parseInt(value);
        break;
      default:
        searchParams[key] = value;
    }
    /* if (value.includes(",") || ["category", "propertyType"].includes(key)) {
      searchParams[key] = value.split(",");
    } else {
      searchParams[key] = value;
    } */
  });
  return searchParams;
};

export function getQueryString(params: SearchParams): URLSearchParams {
  let newParams: SearchParams = {} as SearchParams;
  Object.keys(params).forEach((key) => {
    if (allowedSearchParams.includes(key) && !!params[key as keyof SearchParams]) {
      let finalValue;
      if (Array.isArray(params[key as keyof SearchParams])) {
        finalValue = (params[key as keyof SearchParams] as string[]).filter((item) => !!item).join(",");
      } else {
        finalValue = params[key as keyof SearchParams];
      }
      newParams = { ...newParams, ...{ [key]: finalValue } };
    }
  });
  Object.entries(newParams).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      delete newParams[key as keyof SearchParams];
    }
  });
  delete newParams.withFilters;
  delete newParams.withLocations;
  delete newParams.withResults;
  delete newParams.price;
  delete newParams.area;
  return new URLSearchParams(newParams as Record<string, any>);
}

export const cleanParams = (params: SearchParams) => {
  const cleanedCounterParams = { ...params };
  delete cleanedCounterParams.locale;
  delete cleanedCounterParams.currency;
  delete cleanedCounterParams.measurement;
  Object.entries(cleanedCounterParams).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "" || (value as any[]).length === 0) {
      delete cleanedCounterParams[key as keyof SearchParams];
    }
  });
  return cleanedCounterParams;
};

export const getAvailableFilters = async (params: SearchParams, filter: string) => {};

const catalogItemIncludes = (param: undefined | string | string[] | number | number[], key: string, item: CommercetoolsProject): boolean => {
  if (Array.isArray(param)) {
    return param.filter((value) => (item[key as keyof CommercetoolsProject] as any[])?.includes(value as any)).length > 0;
  } else {
    return (item[key as keyof CommercetoolsProject] as string)?.includes(param as any);
  }
};

export const performSearch = async (params: Record<string, any>): Promise<CommercetoolsProject[]> => {
  let table;
  let collection;
  if (params?.type === "project") {
    table = db.PROJECTS;
  } else {
    table = db.PROPERTY_TYPE;
  }

  const cleanedParams = cleanParams({ ...params });
  if (["category", PRODUCT_TYPE.PROPERTY_TYPE, "bedrooms"].filter((value) => Object.keys(cleanedParams)?.includes(value as any)).length > 0) {
    if (cleanedParams.category) {
      collection = await table.where("category").anyOf(cleanedParams.category);
      delete cleanedParams.category;
    } else if (cleanedParams.propertyType) {
      collection = await table.where(PRODUCT_TYPE.PROPERTY_TYPE).anyOf(cleanedParams.propertyType);
      delete cleanedParams.propertyType;
    } else {
      collection = await table.where("bedrooms").anyOf(cleanedParams.bedrooms!);
      delete cleanedParams.bedrooms;
    }
  } else {
    collection = await table.toCollection();
  }
  Object.entries(cleanedParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.filter((item) => !!item).length === 0) {
        delete cleanedParams[key as keyof SearchParams];
      }
    } else if (!!!value) {
      delete cleanedParams[key as keyof SearchParams];
    }
  });
  const paramKeys = Object.keys(cleanedParams);

  if (paramKeys.length > 0) {
    for (const key of paramKeys) {
      const value = cleanedParams[key as keyof SearchParams] as undefined | string | string[] | number | number[];
      //@ts-ignore
      collection = await collection.filter((item: CommercetoolsProject) => {
        switch (key) {
          case "bedrooms":
            return catalogItemIncludes(value, "bedrooms", item);
          case "unitViews":
            return catalogItemIncludes(value, "unitViews", item);
          case "bathrooms":
            return catalogItemIncludes(value, "bathrooms", item);
          case PRODUCT_TYPE.PROPERTY_TYPE:
            return catalogItemIncludes(value, PRODUCT_TYPE.PROPERTY_TYPE, item);
          case "brands":
            return catalogItemIncludes(value, "brands", item);
          case "price":
            const priceMax = item?.priceMinCurrencies?.[params.currency] ? item.priceMinCurrencies[params.currency] : 0;
            const priceMin = item?.priceMaxCurrencies?.[params.currency] ? item.priceMaxCurrencies[params.currency] : 0;
            const priceInRange = value ? priceMax <= (value as number[])[1] && priceMin >= (value as number[])[0] : true;
            return priceInRange;
          case "area":
            const areaMax = item?.areaMaxConversion?.[params.measurement] ? item.areaMaxConversion[params.measurement] : 0;
            const areaMin = item?.areaMinConversion?.[params.measurement] ? item.areaMinConversion[params.measurement] : 0;
            const areaInRange = value ? areaMax <= (value as number[])[1] && areaMin >= (value as number[])[0] : true;
            return areaInRange;
          case "deliveryDate":
            return (value as string[])?.includes(`${(item.deliveryDate as Date).getFullYear()}`);
          case "status":
            if (value === "any") return true;
            return item.status === value;
          case "isFurnished":
            if (value === "any") return true;
            return item?.isFurnished;
          case "onlyAvailable":
            return value ? item.inventoryAvailable : true;
          case "vTour":
            // return item.vTour === (value === true || value === "true");
            // Accepts value as string or boolean, and compares correctly
            if (typeof value === "string") {
              return item.vTour === (value === "true");
            }
            if (typeof value === "boolean") {
              return item.vTour === value;
            }
            return true;
          case "webIE":
            // Accepts value as string or boolean, and compares correctly
            if (typeof value === "string") {
              return item.webIE === (value === "true");
            }
            if (typeof value === "boolean") {
              return item.webIE === value;
            }
            return true;
          case "others":
            if (Array.isArray(value) && (value as string[]).includes("goldenVisa")) {
              return item.goldenVisa === true;
            }
          case "onlineExclusive":
            return value ? item.exclusive : true;
          default:
            return true;
        }
      });
    }
  }
  // if (!!cleanedParams.sort && cleanedParams.sort !== DEFAULT_SORT) {
  if (!!cleanedParams.sort) {
    const [value, direction] = cleanedParams?.sort?.split("+");
    collection = await collection.sortBy(value);
    if (direction) {
      collection = await collection.reverse();
    }
    if (value === "deliveryDate" && !direction) {
      const results = collection;
      results.sort((a: any, b: any) => {
        const yearA = a.deliveryDate instanceof Date ? a.deliveryDate.getFullYear() : new Date(a.deliveryDate).getFullYear();
        const yearB = b.deliveryDate instanceof Date ? b.deliveryDate.getFullYear() : new Date(b.deliveryDate).getFullYear();
        return yearA - yearB;
      });
      collection = results;
    }
  } else {
    collection = await collection.sortBy("serverSorting");
  }
  const uniqueMap = new Map();
  collection.forEach((item: CommercetoolsProject) => {
    if (!uniqueMap.has(item.id)) {
      uniqueMap.set(item.id, item);
    }
  });

  const uniqueCollection = Array.from(uniqueMap.values());
  if (params?.type === "project" && !cleanedParams.sort) {
    const sortedCollection = uniqueCollection?.sort((a, b) => {
      const isInvalidA = String(a.launchDate).includes("9999");
      const isInvalidB = String(b.launchDate).includes("9999");

      if (isInvalidA && isInvalidB) return 0;
      if (isInvalidA) return 1; // a is invalid → move to bottom
      if (isInvalidB) return -1; // b is invalid → move to bottom

      return new Date(b.launchDate).getTime() - new Date(a.launchDate).getTime(); // Newest first
    });
    return sortedCollection;
  }
  return uniqueCollection;
};
